import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, KeyRound, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, TOKEN_KEY } from "@/lib/api";
import { T } from "@/lib/testIds";

export function AdminActionsBar() {
  const [openEmail, setOpenEmail] = useState(false);
  const [openPass, setOpenPass] = useState(false);

  return (
    <>
      <div className="flex items-center gap-6">
        <button data-testid={T.admin.sendTestEmail} onClick={() => setOpenEmail(true)} className="btn-ghost">
          <Mail size={14} /> Send test email
        </button>
        <button data-testid={T.admin.changePassphrase} onClick={() => setOpenPass(true)} className="btn-ghost">
          <KeyRound size={14} /> Change passphrase
        </button>
      </div>
      <TestEmailDialog open={openEmail} onClose={() => setOpenEmail(false)} />
      <ChangePassphraseDialog open={openPass} onClose={() => setOpenPass(false)} />
    </>
  );
}

function Dialog({ open, onClose, children }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[var(--bg)] border border-[var(--ink)] p-8 sm:p-10 relative"
          >
            <button onClick={onClose} className="absolute top-5 right-5 hover:rotate-90 transition-transform duration-500" aria-label="Close">
              <X size={20} />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function TestEmailDialog({ open, onClose }) {
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/admin/send-test-welcome", to ? { to } : {});
      setResult(data);
      if (data.ok) toast.success("Preview sent.");
      else toast.error("Provider blocked delivery — see details below.");
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || "Failed" });
      toast.error("Send failed.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-3">Preview</p>
      <h3 className="font-serif text-3xl mb-3">Send a welcome email preview</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-8 leading-relaxed">
        Fires the editorial welcome template. Defaults to the studio email (<span className="text-[var(--ink)]">STUDIO_EMAIL</span> from .env). Provide any address below to override.
      </p>
      <label className="block">
        <span className="block text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">To (optional)</span>
        <input
          data-testid={T.admin.testEmailInput}
          className="editorial-input"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="you@example.com — defaults to studio email"
        />
      </label>
      <div className="flex justify-end mt-8">
        <button data-testid={T.admin.testEmailSubmit} disabled={loading} onClick={send} className="btn-primary">
          {loading ? "Sending…" : "Send preview"} <ArrowRight size={14} />
        </button>
      </div>
      {result && (
        <div data-testid={T.admin.testEmailResult} className="mt-6 border-t border-[var(--line)] pt-4 text-xs leading-relaxed">
          {result.ok ? (
            <p className="text-[var(--sage-deep)] flex items-center gap-2"><Check size={14}/> Delivered to {result.to}. HTTP {result.status}.</p>
          ) : (
            <div>
              <p className="text-[var(--ink)] font-medium mb-2">Provider response{result.status ? ` · HTTP ${result.status}` : ""}</p>
              <pre className="text-[11px] leading-relaxed whitespace-pre-wrap text-[var(--ink-soft)] bg-[var(--bg-alt)] p-3 border border-[var(--line)]">{result.detail || result.error || JSON.stringify(result, null, 2)}</pre>
              <p className="text-[11px] text-[var(--ink-soft)] mt-3 italic">
                Tip: Emergent's shared Resend sender blocks Gmail/consumer inboxes by default. To reliably reach <span className="text-[var(--ink)]">budstheflower@gmail.com</span>, verify a sending domain (e.g. mail.budstheflowercart.com) with Resend. Meanwhile, use <span className="text-[var(--ink)]">delivered@resend.dev</span> for a guaranteed test delivery.
              </p>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

function ChangePassphraseDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (next.length < 8) { toast.error("New passphrase must be 8+ characters."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/change-passphrase", {
        current_passphrase: current,
        new_passphrase: next,
      });
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        api.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
      }
      toast.success("Passphrase rotated. Share the new one privately with your delivery lead.");
      setCurrent(""); setNext(""); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-3">Security</p>
      <h3 className="font-serif text-3xl mb-3">Rotate studio passphrase</h3>
      <p className="text-sm text-[var(--ink-soft)] mb-8 leading-relaxed">
        The new passphrase is hashed and stored in the database. Share it privately with your delivery lead only.
      </p>
      <div className="space-y-6">
        <label className="block">
          <span className="block text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">Current passphrase</span>
          <input data-testid={T.admin.passCurrent} className="editorial-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">New passphrase (8+ characters)</span>
          <input data-testid={T.admin.passNew} className="editorial-input" type="password" value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
      </div>
      <div className="flex justify-end mt-8">
        <button data-testid={T.admin.passSubmit} disabled={loading || !current || !next} onClick={submit} className="btn-primary">
          {loading ? "Rotating…" : "Rotate passphrase"} <ArrowRight size={14} />
        </button>
      </div>
    </Dialog>
  );
}
